import { useState, useEffect, useRef } from 'react';
import { Title, Text, Paper, Loader, Stack, Card, Progress, Button, Alert, Group } from '@mantine/core';
import { FormData, AnalysisData } from '@/types';
import { generateAnalysis } from '@/api/analysis';
import { IconAlertCircle, IconRefresh } from '@tabler/icons-react';
import logger from '@/utils/logger';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { verifyRecaptchaToken } from '@/utils/recaptcha';

interface PostAnalysisProps {
  formData: FormData;
  preloadedPersonas?: Promise<any> | null;
  onAnalysisComplete: (analysisData: AnalysisData) => void;
}

const PostAnalysis = ({ formData, preloadedPersonas, onAnalysisComplete }: PostAnalysisProps) => {
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState('Initializing analysis');
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  
  // Ref to track if analysis has been run
  const hasRunAnalysis = useRef(false);
  
  // Get reCAPTCHA execution function
  const { executeRecaptcha } = useGoogleReCaptcha();
  
  // Setup progress tracking event listeners
  useEffect(() => {
    // Listen for progress events from the API
    const handleProgress = (event: CustomEvent) => {
      const { stage, percentage } = event.detail;
      setProgress(percentage);
      setCurrentStage(stage);
      logger.info(`Analysis progress update: ${percentage}% - ${stage}`);
    };

    // Add event listener
    window.addEventListener('analysis-progress', handleProgress as EventListener);
    
    // Cleanup
    return () => {
      window.removeEventListener('analysis-progress', handleProgress as EventListener);
    };
  }, []);
  
  const analyzePost = async () => {
    // Skip if we've already run the analysis
    if (hasRunAnalysis.current) {
      logger.debug('Analysis already in progress or completed, skipping duplicate run');
      return;
    }
    
    // Mark analysis as started
    hasRunAnalysis.current = true;
    
    logger.info('Starting post analysis process');
    setLoading(true);
    setProgress(5); // Start with a small initial progress
    setCurrentStage('Preparing analysis');
    setError(null);
    setErrorDetails(null);
    
    // Setup progress tracking
    const updateProgress = (stage: string, percentage: number) => {
      // Dispatch a custom event for progress updates
      const progressEvent = new CustomEvent('analysis-progress', {
        detail: { stage, percentage }
      });
      window.dispatchEvent(progressEvent);
    };
    
    // Start the API call with progress tracking
    try {
      updateProgress('Initializing audience analysis', 10);
      
      // Verify with reCAPTCHA before proceeding
      let recaptchaVerified = true;
      if (executeRecaptcha) {
        try {
          const token = await executeRecaptcha('analysis_request');
          logger.info('reCAPTCHA executed', { token: token.substring(0, 10) + '...' });
          
          // In a production environment, you'd verify this token with Google's API
          // For now, we'll assume it's valid or use a fake verification
          recaptchaVerified = await verifyRecaptchaToken(token);
          
          if (!recaptchaVerified) {
            throw new Error('reCAPTCHA verification failed. Please try again.');
          }
        } catch (recaptchaError) {
          logger.error('reCAPTCHA error', recaptchaError);
          // Allow the request to proceed if reCAPTCHA fails but log the error
          // In a production environment, you might want to block the request
        }
      }
      
      // Start with either preloaded personas or generate them
      if (preloadedPersonas) {
        updateProgress('Loading audience personas', 15);
      } else {
        updateProgress('Creating personas based on your industry and audience', 15);
      }
      
      // Start actual API call
      const analysisPromise = generateAnalysis(formData, preloadedPersonas);
      
      // Update progress with more stages and humor
      await new Promise(resolve => setTimeout(resolve, 3500));
      updateProgress('Analyzing content for audience relevance', 25);
      
      await new Promise(resolve => setTimeout(resolve, 3500));
      updateProgress('Checking for accidental "Open to Work" banners', 35);
      
      await new Promise(resolve => setTimeout(resolve, 3500));
      updateProgress('Removing all uses of "excited to announce"', 45);
      
      await new Promise(resolve => setTimeout(resolve, 3500));
      updateProgress('Running background checks on humblebrags…', 55);
      
      await new Promise(resolve => setTimeout(resolve, 3500));
      updateProgress('Polishing buzzwords', 65);
      
      await new Promise(resolve => setTimeout(resolve, 3500));
      updateProgress('Bribing the LinkedIn algo with emojis', 75);
      
      await new Promise(resolve => setTimeout(resolve, 3500));
      updateProgress('Injecting just enough humility…', 80);
      
      await new Promise(resolve => setTimeout(resolve, 3500));
      updateProgress('Polishing final results and insights', 85);
      
      await new Promise(resolve => setTimeout(resolve, 2500));
      updateProgress('Preparing to wow you with detailed feedback', 90);
      
      await new Promise(resolve => setTimeout(resolve, 2500));
      updateProgress('Almost done! Just adding those finishing touches', 95);
      
      // Wait for the API call to complete
      logger.info('Waiting for Gemini API analysis to complete');
      const analysisData = await analysisPromise;
      logger.info(`Analysis complete with ${analysisData.audiencePersonas.length} personas and ${analysisData.feedback.length} feedback items`);
      
      updateProgress('Analysis complete', 100);
      
      // Short delay before moving to next screen
      await new Promise(resolve => setTimeout(resolve, 1000));
      logger.info('Moving to audience feedback view');
      onAnalysisComplete(analysisData);
    } catch (error: any) {
      logger.error('Analysis failed:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Analysis failed. The AI model couldn\'t process your request.';
      setError(errorMessage);
      logger.error('Setting error message:', errorMessage);
      
      // Provide more detailed error information based on error message
      if (errorMessage.includes('API key')) {
        setErrorDetails('Please check that your Gemini API key is valid and has access to the Gemini Pro model.');
        logger.error('API key related error');
      } else if (errorMessage.includes('parsing')) {
        setErrorDetails('The AI model response couldn\'t be parsed correctly. This might be due to changes in the API response format.');
        logger.error('JSON parsing error');
      } else if (errorMessage.includes('network')) {
        setErrorDetails('Please check your internet connection and try again.');
        logger.error('Network connectivity error');
      } else {
        setErrorDetails('This could be due to API access issues, rate limiting, or incorrect API configuration.');
        logger.error('General API error');
      }
      
      setCurrentStage('Analysis failed');
    } finally {
      setLoading(false);
      logger.info('Analysis process ended');
    }
  };
  
  useEffect(() => {
    analyzePost();
  }, [formData]);
  
  return (
    <Paper p="xl" className='responsive-container' radius="md" withBorder>
      <Stack gap="xl" align="center">
        <Title order={2} ta="center">
          Analysing Your Post
        </Title>
        
        <Text c="dimmed" ta="center" size="lg">
          We are evaluating your post with an audience tailored to your industry, role, and target demographic based on your questionnaire responses.
        </Text>
        
        <Card withBorder p="xl" radius="md" style={{ width: '100%' }}>
          <Stack gap="md">
            <Progress 
              value={error ? 0 : progress} 
              size="xl" 
              radius="xl" 
              striped={loading} 
              animated={loading} 
              color={error ? 'red' : 'blue'} 
            />
            
            {error ? (
              <Alert icon={<IconAlertCircle size="1rem" />} title="Analysis Failed" color="red">
                <Stack gap="md">
                  <Text>{error}</Text>
                  {errorDetails && <Text size="sm">{errorDetails}</Text>}
                  <Group justify="center">
                    <Button 
                      leftSection={<IconRefresh size="1rem" />}
                      onClick={analyzePost}
                      variant="outline"
                    >
                      Retry Analysis
                    </Button>
                  </Group>
                </Stack>
              </Alert>
            ) : (
              <Stack gap="md" align="center">
                {loading && <Loader size="md" />}
                <Text ta="center" size="lg" fw={500}>
                  {currentStage}
                </Text>
                <Text ta="center" c="dimmed">
                  {loading
                    ? 'This may take a moment as we analyze your post and gather feedback.'
                    : 'Analysis complete! Viewing results...'}
                </Text>
              </Stack>
            )}
          </Stack>
        </Card>
      </Stack>
    </Paper>
  );
};

export default PostAnalysis; 